import { useState, useRef, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import useReCaptchaV3 from "./useReCaptChaV3";
import { CallApiVerifyCaptcha_V3 } from "../Api/Enforcement";
import { ShowCustomErrorModal } from "../Components/Atoms/CustomErrorModal";

const ERROR_MESSAGES = {
  RECAPTCHA_INVALID: "Đã xảy ra lỗi : \nreCAPTCHA không hợp lệ!",
  RECAPTCHA_LOADING_ERROR: "Không thể tải reCAPTCHA. Vui lòng tải lại trang.",
  RECAPTCHA_REQUIRED: "Vui lòng hoàn thành xác minh reCAPTCHA.",
};

export default function useReCaptcha(actionName, redirectPath) {
  const isDefaultV3 = process.env.REACT_APP_RECAPTCHA_DEFAULT === "3";
  const [searchParams] = useSearchParams();
  const version = searchParams.get("version");
  const reCaptchaV2 = useRef();

  const [isReCaptchaReady, executeReCaptcha] = useReCaptchaV3();
  const [isLoadReCaptChaV3, setIsLoadReCaptChaV3] = useState(false);
  const [recaptchaLoading, setRecaptchaLoading] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState(null);
  const recaptchaScriptRef = useRef(null);

  const handleReCaptchaVerify = useCallback(async () => {
    try {
      setIsLoadReCaptChaV3(true);
      const token = await executeReCaptcha("preTest");

      CallApiVerifyCaptcha_V3(token, (res) => {
        setIsLoadReCaptChaV3(false);
        if (res !== "Success") {
          window.location.replace(redirectPath + "?version=2");
        }
      });
    } catch (error) {
      setIsLoadReCaptChaV3(false);
      ShowCustomErrorModal({ message: ERROR_MESSAGES.RECAPTCHA_INVALID });
      window.location.replace(redirectPath + "?version=2");
    }
  }, [executeReCaptcha, redirectPath]);

  useEffect(() => {
    if (version === "2" || !isDefaultV3) {
      return;
    }

    if (isReCaptchaReady && isDefaultV3) {
      handleReCaptchaVerify();
    }
  }, [handleReCaptchaVerify, isDefaultV3, isReCaptchaReady, version]);

  const renderReCaptcha = useCallback(() => {
    if (!window.grecaptcha || !reCaptchaV2.current) {
      return;
    }

    try {
      // Check if already rendered
      const existingRecaptcha =
        reCaptchaV2.current.querySelector(".g-recaptcha");

      if (existingRecaptcha) {
        try {
          window.grecaptcha.reset();
        } catch (resetError) {
          console.error("Failed to reset reCAPTCHA:", resetError);
        }
        return;
      }

      // Render new reCAPTCHA
      window.grecaptcha.render(reCaptchaV2.current, {
        sitekey: process.env.REACT_APP_RECAPTCHA_KEY_V2,
        callback: () => {
          setRecaptchaError(null);
        },
        "expired-callback": () => {
          setRecaptchaError(
            "Mã xác thực reCAPTCHA đã hết hạn. Vui lòng xác minh lại."
          );
        },
        "error-callback": () => {
          setRecaptchaError(
            "Lỗi tải reCAPTCHA. Vui lòng thử lại hoặc tải lại trang."
          );
        },
      });
    } catch (error) {
      // Handle "already rendered" error specifically
      if (error.message && error.message.includes("already been rendered")) {
        try {
          window.grecaptcha.reset();
        } catch (resetError) {
          console.error("Failed to reset reCAPTCHA:", resetError);
        }
      } else {
        setRecaptchaError(ERROR_MESSAGES.RECAPTCHA_LOADING_ERROR);
      }
    }
  }, []);

  useEffect(() => {
    if (version === "2" || !isDefaultV3) {
      let scriptLoaded = false;
      let checkInterval = null;

      // Create a more robust cleanup function
      const cleanup = () => {
        if (checkInterval) {
          clearInterval(checkInterval);
        }

        if (recaptchaScriptRef.current) {
          recaptchaScriptRef.current.remove();
          recaptchaScriptRef.current = null;
        }

        // Don't undefine grecaptcha globally, just reset if needed
        if (window.grecaptcha && window.grecaptcha.reset && scriptLoaded) {
          try {
            window.grecaptcha.reset();
          } catch (e) {
            // console.error("Error resetting reCAPTCHA:", e);
          }
        }
      };

      // Load reCAPTCHA script
      const loadRecaptchaScript = () => {
        setRecaptchaLoading(true);
        setRecaptchaError(null);

        // Check if script already exists
        const existingScript = document.querySelector(
          'script[src*="recaptcha/api.js"]'
        );
        if (existingScript) {
          existingScript.remove();
        }

        recaptchaScriptRef.current = document.createElement("script");
        recaptchaScriptRef.current.src =
          "https://www.google.com/recaptcha/api.js?render=explicit";
        recaptchaScriptRef.current.async = true;
        recaptchaScriptRef.current.defer = true;

        recaptchaScriptRef.current.onload = () => {
          scriptLoaded = true;

          // Check for grecaptcha to be fully loaded
          checkInterval = setInterval(() => {
            if (window.grecaptcha && window.grecaptcha.render) {
              clearInterval(checkInterval);
              setRecaptchaLoading(false);
              renderReCaptcha();
            }
          }, 100);

          // Set timeout to avoid infinite checking
          setTimeout(() => {
            if (checkInterval) {
              clearInterval(checkInterval);
              if (!window.grecaptcha || !window.grecaptcha.render) {
                setRecaptchaError(ERROR_MESSAGES.RECAPTCHA_LOADING_ERROR);
                setRecaptchaLoading(false);
              }
            }
          }, 5000);
        };

        recaptchaScriptRef.current.onerror = () => {
          setRecaptchaLoading(false);
          setRecaptchaError(ERROR_MESSAGES.RECAPTCHA_LOADING_ERROR);
        };

        document.head.appendChild(recaptchaScriptRef.current);
      };

      loadRecaptchaScript();

      // Handle page visibility changes
      const handleVisibilityChange = () => {
        if (document.visibilityState === "visible" && scriptLoaded) {
          // Re-render reCAPTCHA when page becomes visible again
          setTimeout(renderReCaptcha, 300);
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);

      return () => {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
        cleanup();
      };
    }
  }, [version, isDefaultV3, renderReCaptcha]);

  const resetReCaptcha = useCallback(() => {
    if (version === "2" || !isDefaultV3) {
      try {
        window.grecaptcha?.reset();
      } catch (error) {
        // console.error("Error resetting reCAPTCHA:", error);
      }
    }
  }, [version, isDefaultV3]);

  const getReCaptchaToken = useCallback(async () => {
    let token = { token_v2: "", token_v3: "" };

    if (version === "2" || !isDefaultV3) {
      // Lấy token v2
      const v2Token = window.grecaptcha?.getResponse();
      if (!v2Token) {
        throw new Error(ERROR_MESSAGES.RECAPTCHA_REQUIRED);
      }
      token.token_v2 = v2Token;
    } else {
      // Lấy token v3
      try {
        token.token_v3 = await executeReCaptcha(actionName);
      } catch (error) {
        throw new Error(ERROR_MESSAGES.RECAPTCHA_INVALID);
      }
    }

    return token;
  }, [version, isDefaultV3, executeReCaptcha, actionName]);

  return {
    version,
    reCaptchaV2,
    isReCaptchaReady,
    isLoadReCaptChaV3,
    recaptchaLoading,
    recaptchaError,
    getReCaptchaToken,
    resetReCaptcha,
    isDefaultV3,
  };
}
